import * as d3 from 'd3';
import {
  config,
  GENDER,
  isPregnancyLoss,
  isOngoingPregnancy,
  isGameteDonorNode,
  isSurrogateNode,
  isReproSatelliteNode,
  reproSatelliteGenderShape
} from './config';
import { genderIconMap } from './utils';

// Reproductive-node symbol resolvers (Track A render), extracted from Canvas.jsx so
// the shape/size DECISION is pure and unit-testable (the D3 draw itself needs a
// browser). Single source of truth for a node's symbol SIZE and TYPE, used by BOTH
// the visible symbol path and the clip-path twin so the two can never drift (the
// disease pie and carrier perimeter clip to the twin). Every repro branch is gated on
// config.ENABLE_REPRO_NODES AND a repro predicate; a node without reproKind — and
// every node when the flag is off — falls through to the identical non-repro tail, so
// the golden corpus stays byte-identical (proven with the flag both off and on).

// nodeSymbolArea now lives in config.js (pure, no d3) so the d3-free engine
// (path.js/layout.js) can share the one symbol-size source; re-exported here so the
// render + tests keep importing it from reproSymbol alongside nodeSymbolType.
export { nodeSymbolArea } from './config';

export function nodeSymbolType(d) {
  if (config.ENABLE_REPRO_NODES) {
    if (isPregnancyLoss(d)) return d3.symbolTriangle; // SAB / TOP / ECT
    if (isOngoingPregnancy(d)) {
      const preg = d.pregnancy || (d.data && d.data.pregnancy);
      if (!preg || !preg.fetalSexKnown) return d3.symbolDiamond2; // sex unknown
      // fetal sex known → fall through to the gender square/circle
    } else if (isReproSatelliteNode(d)) {
      const shape = reproSatelliteGenderShape(d); // sperm→□, ovum/surrogate→○
      if (shape === GENDER.male) return d3.symbolSquare;
      if (shape === GENDER.female) return d3.symbolCircle;
      // embryo donor / unset → fall through to gender
    }
    // stillbirth & liveBirth pregnancies fall through → ordinary person symbol
  }
  const gender = genderIconMap(d); // ← unchanged non-repro path
  if (gender === GENDER.male) return d3.symbolSquare;
  else if (gender === GENDER.female) return d3.symbolCircle;
  else return d3.symbolDiamond2;
}

// Interior glyph letter for a repro node: P (ongoing pregnancy) / D (donor) /
// S (surrogate). Loss triangles + stillbirths carry a label instead, not a letter.
export function reproGlyphLetter(d) {
  if (isOngoingPregnancy(d)) return 'P';
  if (isGameteDonorNode(d)) return 'D';
  if (isSurrogateNode(d)) return 'S';
  return '';
}

// Endpoints (node-local coords, centred on the symbol origin) of the diagonal struck
// through a Termination-of-Pregnancy loss triangle (Bennett Fig 4). Unlike a deceased
// slash (a symmetric 45° line), the TOP strike runs PARALLEL to the triangle's LEFT
// EDGE — slope −√3 (≈60°, steeper) — and extends a margin past the apex and base so it
// crosses the WHOLE symbol. Geometry from d3.symbolTriangle: for a triangle of area
// (SYMBOL_SIZE·REPRO_LOSS_SCALE)² the apex sits at (0,−2m), the base at y=+m and each
// base corner at ±√3·m, with the centroid at the origin, where m = L / 3^0.75. The line
// passes through the centroid (y = −√3·x), from below the base (lower-left) to above the
// apex (upper-right). Pure (no d3) so the geometry is unit-testable.
export function terminationStrikeEndpoints() {
  const L = config.SYMBOL_SIZE * config.REPRO_LOSS_SCALE;
  const m = L / Math.pow(3, 0.75); // d3.symbolTriangle scale for area L²
  const SQRT3 = Math.sqrt(3);
  // Asymmetric overhang so the strike reads balanced on the triangle: a SMALL margin
  // past the apex (top) and a LARGER one past the base (bottom). A symmetric overhang
  // poked out too far at the top and not enough at the bottom. Both ends stay on the
  // same y=−√3·x line through the centroid — this only slides the drawn segment down.
  const topOverhang = 0.25 * m; // past the apex (upper-right)
  const bottomOverhang = 0.75 * m; // past the base (lower-left)
  const yBottom = m + bottomOverhang; // lower-left end (below the base)
  const yTop = -2 * m - topOverhang; // upper-right end (above the apex)
  return {
    x1: -yBottom / SQRT3, // on y = −√3·x ⇒ x = −y/√3
    y1: yBottom,
    x2: -yTop / SQRT3,
    y2: yTop
  };
}
